import type { EChartsOption } from 'echarts';
import ReactEcharts from 'echarts-for-react';
import React, { useMemo, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { getChartColors, useEChartsTheme, useTheme } from '../../../../theme';
import { chartImageExtractor } from '../../../../helpers/chartImageExtractor';
import type { PieChart as PieChartType } from '../../../../types/charts';

interface PieChartProps {
  chartData: PieChartType;
  width?: number;
  height?: number;
  chartId?: string;
}

const PieChart: React.FC<PieChartProps> = ({ chartData, width = 400, height = 300, chartId }) => {
  const theme = useTheme();
  const echartsTheme = useEChartsTheme();
  const chartRef = useRef<any>(null);

  const option: EChartsOption = useMemo(() => {
    const { data, config, title } = chartData;
    const defaultColors = getChartColors(theme, 'default');

    return {
      title: title
        ? {
            text: title,
            left: 'left',
            top: '2%',
            textStyle: {
              fontSize: 16,
              fontWeight: 500,
              color: echartsTheme.textColor
            }
          }
        : undefined,
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)',
        backgroundColor: echartsTheme.tooltipBg,
        borderColor: echartsTheme.borderColor,
        borderWidth: 1,
        textStyle: {
          color: echartsTheme.textColor
        },
        confine: true,
        z: 9999
      },
      legend: config?.showLabels
        ? {
            orient: 'vertical',
            left: 'left',
            top: 'middle',
            textStyle: {
              color: echartsTheme.labelColor
            },
            icon: 'circle'
          }
        : undefined,
      series: [
        {
          name: title || 'Data',
          type: 'pie',
          radius: '70%',
          center: ['50%', '50%'],
          data: data.map((item, index) => ({
            value: item.value,
            name: item.name,
            itemStyle: {
              color:
                item.color || config?.colors?.[index] || defaultColors[index % defaultColors.length]
            }
          })),
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          label: {
            show: config?.showLabels !== false,
            formatter: '{b}: {d}%',
            color: echartsTheme.labelColor
          },
          labelLine: {
            show: config?.showLabels !== false
          }
        }
      ]
    };
  }, [chartData, theme, echartsTheme]);

  // Register chart instance for image extraction
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (chartId) {
        chartImageExtractor.unregisterChart(chartId);
      }
    };
  }, [chartId]);

  const onChartReady = (chart: any) => {
    if (chartId) {
      chartImageExtractor.registerChart(chartId, chart);
    }
  };

  return (
    <ChartContainer>
      <ReactEcharts
        ref={chartRef}
        option={option}
        style={{ width: `${width}px`, height: `${height}px` }}
        opts={{ renderer: 'canvas' }}
        onChartReady={onChartReady}
      />
    </ChartContainer>
  );
};

export default PieChart;

const ChartContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;
